import { defineStore } from 'pinia'
import { subscriptionService } from '@/services/subscriptionService'
import type { PaymentMethod, SetupIntent } from '@/types/subscription'
import { handleAuthError, extractErrorMessage } from '@/utils/storeHelpers'

export const usePaymentMethodsStore = defineStore('paymentMethods', {
  state: () => ({
    paymentMethods: [] as PaymentMethod[],
    loading: false,
    error: null as string | null,
    isAddingPaymentMethod: false,
  }),

  getters: {
    hasPaymentMethods: (state) => state.paymentMethods.length > 0,
    
    defaultPaymentMethod: (state) => {
      return state.paymentMethods.find(pm => pm.is_default)
    },
    
    getPaymentMethodById: (state) => (id: number) => {
      return state.paymentMethods.find(pm => pm.id === id)
    },
  },

  actions: {
    async fetchPaymentMethods() {
      this.loading = true
      this.error = null

      try {
        const response = await subscriptionService.getPaymentMethods()
        this.paymentMethods = response.data
      } catch (error: any) {
        if (handleAuthError(error)) {
          this.error = 'Current user is not authorized to access payment methods'
          return
        }

        this.error = extractErrorMessage(error, 'Failed to fetch payment methods')
      } finally {
        this.loading = false
      }
    },

    async createSetupIntent(customerId: string): Promise<SetupIntent> {
      try {
        return await subscriptionService.createSetupIntent(customerId)
      } catch (error: any) {
        if (handleAuthError(error)) {
          throw new Error('Current user is not authorized to create setup intent')
        }

        throw error
      }
    },

    async attachPaymentMethod(paymentMethodId: string, customerId: string) {
      this.isAddingPaymentMethod = true

      try {
        const response = await subscriptionService.attachPaymentMethod(paymentMethodId, customerId)

        // Refresh payment methods
        await this.fetchPaymentMethods()
        return response.data
      } catch (error: any) {
        if (handleAuthError(error)) {
          throw new Error('Current user is not authorized to attach payment method')
        }

        throw error
      } finally {
        this.isAddingPaymentMethod = false
      }
    },

    async setDefaultPaymentMethod(paymentMethodId: string, customerId: string) {
      try {
        const response = await subscriptionService.setDefaultPaymentMethod(paymentMethodId, customerId)

        // Refresh payment methods
        await this.fetchPaymentMethods()
        return response.data
      } catch (error: any) {
        if (handleAuthError(error)) {
          throw new Error('Current user is not authorized to set default payment method')
        }

        throw error
      }
    },

    async deletePaymentMethod(paymentMethodId: number) {
      try {
        await subscriptionService.deletePaymentMethod(paymentMethodId)

        // Refresh payment methods
        await this.fetchPaymentMethods()
      } catch (error: any) {
        if (handleAuthError(error)) {
          throw new Error('Current user is not authorized to delete payment method')
        }

        throw error
      }
    },

    async createSetupCardSession() {
      this.isAddingPaymentMethod = true

      try {
        const response = await subscriptionService.createSetupCardSession()
        return response.data

      } catch (error: any) {
        if (handleAuthError(error)) {
          throw new Error('Current user is not authorized to create setup card session')
        }

        throw error
      } finally {
        this.isAddingPaymentMethod = false
      }
    },
  }
})

